import { LitElement, html } from 'lit';
import logoUrl from '../assets/logo.png';
import { api } from '../api.js';
import { passwordMeetsRules, passwordChecklist } from '../utils/password-policy.js';

/**
 * /reset-password?token=... — public page to set a new password from a forgot-password
 * e-mail link. No auth required; the token is the credential (single-use, 1-hour expiry,
 * verified server-side). The password policy (complexity + HIBP breach check) is enforced
 * on the backend; we show a live checklist here for feedback.
 */
export class ResetPassword extends LitElement {
  createRenderRoot() { return this; }

  static properties = {
    _password: { type: String, state: true },
    _password2: { type: String, state: true },
    _busy: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _done: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this._password = '';
    this._password2 = '';
    this._busy = false;
    this._error = null;
    this._done = false;
    this._token = new URLSearchParams(window.location.search).get('token') || '';
  }

  async _submit(e) {
    e?.preventDefault?.();
    this._error = null;
    if (!passwordMeetsRules(this._password)) { this._error = 'Je wachtwoord voldoet nog niet aan alle eisen.'; return; }
    if (this._password !== this._password2) { this._error = 'De wachtwoorden komen niet overeen.'; return; }
    this._busy = true;
    try {
      await api.resetPassword(this._token, this._password);
      this._done = true;
    } catch (err) {
      this._error = err.message || 'Wachtwoord opnieuw instellen mislukt';
    } finally {
      this._busy = false;
    }
  }

  render() {
    const field = 'width:100%; padding:10px 12px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-bg,#fff); color:var(--mv-text); box-sizing:border-box;';
    const primary = 'width:100%; padding:12px; border:none; border-radius:8px; background:var(--mv-primary); color:#fff; font-weight:700; font-size:0.95rem; cursor:pointer;';

    return html`
      <div style="min-height:100vh; display:flex; align-items:center; justify-content:center; padding:20px; background:var(--mv-bg,#f8f9fa);">
        <div style="width:100%; max-width:420px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:16px; padding:32px;">
          <img src=${logoUrl} alt="GeoVoice" style="width:120px; height:auto; display:block; margin:0 auto 20px;" />

          ${!this._token ? html`
            <h2 style="text-align:center; color:var(--mv-text); margin:0 0 8px;">Ongeldige link</h2>
            <p style="text-align:center; font-size:0.9rem; color:var(--mv-text-sub,#6c757d);">Deze resetlink is onvolledig. Vraag een nieuwe aan via "Wachtwoord vergeten?" op de inlogpagina.</p>
            <a href="/" style="display:block; text-align:center; margin-top:18px; color:var(--mv-primary); text-decoration:none; font-weight:600;">Naar inloggen</a>
          ` : this._done ? html`
            <h2 style="text-align:center; color:var(--mv-text); margin:0 0 8px;">Wachtwoord gewijzigd ✓</h2>
            <p style="text-align:center; font-size:0.9rem; color:var(--mv-text-sub,#6c757d);">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
            <a href="/" style="display:block; text-align:center; margin-top:18px; padding:12px; border-radius:8px; background:var(--mv-primary); color:#fff; text-decoration:none; font-weight:700;">Naar inloggen</a>
          ` : html`
            <h2 style="text-align:center; color:var(--mv-text); margin:0 0 6px;">Nieuw wachtwoord</h2>
            <p style="text-align:center; font-size:0.88rem; color:var(--mv-text-sub,#6c757d); margin:0 0 18px;">Kies een sterk, uniek wachtwoord.</p>
            ${this._error ? html`<div style="padding:10px 12px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); margin-bottom:14px; font-size:0.88rem;">${this._error}</div>` : ''}
            <form @submit=${this._submit}>
              <label style="display:block; font-size:0.82rem; font-weight:700; color:var(--mv-text-sub,#6c757d); margin-bottom:4px;">Nieuw wachtwoord</label>
              <input type="password" required autocomplete="new-password" .value=${this._password}
                @input=${(e) => { this._password = e.target.value; this._error = null; }} style="${field} margin-bottom:6px;" />
              ${this._password ? passwordChecklist(this._password) : ''}
              <label style="display:block; font-size:0.82rem; font-weight:700; color:var(--mv-text-sub,#6c757d); margin:14px 0 4px;">Herhaal wachtwoord</label>
              <input type="password" required autocomplete="new-password" .value=${this._password2}
                @input=${(e) => { this._password2 = e.target.value; this._error = null; }} style="${field} margin-bottom:18px;" />
              <button type="submit" ?disabled=${this._busy} style="${primary} ${this._busy ? 'opacity:0.7;' : ''}">${this._busy ? 'Bezig…' : 'Wachtwoord instellen'}</button>
            </form>
          `}
        </div>
      </div>
    `;
  }
}

customElements.define('reset-password', ResetPassword);
